import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Trophy, Flame, Target, TrendingUp, Award } from 'lucide-react';
import Header from '../../components/ui/Header';
import leaderboardService from '../../services/leaderboardService';
import LeaderboardCategory from './components/LeaderboardCategory';
import FilterBar from './components/FilterBar';
import ErrorBoundary from '../../components/ErrorBoundary';

const CommunityLeaderboardsDashboard = () => {
  const [filters, setFilters] = useState({
    timePeriod: 'all-time',
    userType: 'all',
    contentCategory: 'all'
  });

  const [leaderboards, setLeaderboards] = useState({
    xpRankings: [],
    streaks: [],
    contentMastery: [],
    risingStars: []
  });

  const [loading, setLoading] = useState({
    xpRankings: true,
    streaks: true,
    contentMastery: true,
    risingStars: true
  });

  const [errors, setErrors] = useState({});

  // Fetch XP Rankings
  useEffect(() => {
    const fetchXPRankings = async () => {
      setLoading(prev => ({ ...prev, xpRankings: true }));
      try {
        const data = await leaderboardService?.getXPLeaderboard({
          userType: filters?.userType,
          timePeriod: filters?.timePeriod,
          limit: 50
        });
        setLeaderboards(prev => ({ ...prev, xpRankings: data || [] }));
        setErrors(prev => ({ ...prev, xpRankings: null }));
      } catch (error) {
        console.error('Error fetching XP rankings:', error);
        setErrors(prev => ({ ...prev, xpRankings: error?.message }));
      } finally {
        setLoading(prev => ({ ...prev, xpRankings: false }));
      }
    };

    fetchXPRankings();
  }, [filters?.userType, filters?.timePeriod]);

  // Fetch Achievement Streaks
  useEffect(() => {
    const fetchStreaks = async () => {
      setLoading(prev => ({ ...prev, streaks: true }));
      try {
        const data = await leaderboardService?.getStreakLeaderboard({
          userType: filters?.userType,
          limit: 50
        });
        setLeaderboards(prev => ({ ...prev, streaks: data || [] }));
        setErrors(prev => ({ ...prev, streaks: null }));
      } catch (error) {
        console.error('Error fetching streaks:', error);
        setErrors(prev => ({ ...prev, streaks: error?.message }));
      } finally {
        setLoading(prev => ({ ...prev, streaks: false }));
      }
    };

    fetchStreaks();
  }, [filters?.userType]);

  // Fetch Content Mastery
  useEffect(() => {
    const fetchContentMastery = async () => {
      setLoading(prev => ({ ...prev, contentMastery: true }));
      try {
        const data = await leaderboardService?.getContentMasteryLeaderboard({
          userType: filters?.userType,
          category: filters?.contentCategory,
          limit: 50
        });
        setLeaderboards(prev => ({ ...prev, contentMastery: data || [] }));
        setErrors(prev => ({ ...prev, contentMastery: null }));
      } catch (error) {
        console.error('Error fetching content mastery:', error);
        setErrors(prev => ({ ...prev, contentMastery: error?.message }));
      } finally {
        setLoading(prev => ({ ...prev, contentMastery: false }));
      }
    };

    fetchContentMastery();
  }, [filters?.userType, filters?.contentCategory]);

  // Fetch Rising Stars
  useEffect(() => {
    const fetchRisingStars = async () => {
      setLoading(prev => ({ ...prev, risingStars: true }));
      try {
        const data = await leaderboardService?.getRisingStarsLeaderboard({
          userType: filters?.userType,
          limit: 50,
          days: filters?.timePeriod === 'daily' ? 1 : filters?.timePeriod === 'weekly' ? 7 : 30
        });
        setLeaderboards(prev => ({ ...prev, risingStars: data || [] }));
        setErrors(prev => ({ ...prev, risingStars: null }));
      } catch (error) {
        console.error('Error fetching rising stars:', error);
        setErrors(prev => ({ ...prev, risingStars: error?.message }));
      } finally {
        setLoading(prev => ({ ...prev, risingStars: false }));
      }
    };

    fetchRisingStars();
  }, [filters?.userType, filters?.timePeriod]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <Helmet>
          <title>Community Leaderboards - RoleCall</title>
          <meta name="description" content="View top-performing students and educators with XP rankings, achievement streaks, and content mastery leaderboards" />
        </Helmet>

        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl mb-4">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">
              Community Leaderboards
            </h1>
            <p className="text-xl text-purple-200 max-w-2xl mx-auto">
              Celebrate top performers and rising stars in the RoleCall community
            </p>
          </div>

          {/* Filters */}
          <FilterBar filters={filters} onFilterChange={setFilters} />

          {/* Error Display */}
          {Object.values(errors)?.some(error => error) && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6">
              <p className="text-red-800 text-sm">
                Some leaderboards couldn't load. Please try refreshing the page.
              </p>
            </div>
          )}

          {/* Leaderboards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* XP Rankings */}
            <LeaderboardCategory
              title="XP Rankings"
              icon={<Trophy className="w-6 h-6 text-yellow-500" />}
              entries={leaderboards?.xpRankings || []}
              loading={loading?.xpRankings || false}
              emptyMessage="No XP rankings available yet. Start earning XP to appear here!"
            />

            {/* Achievement Streaks */}
            <LeaderboardCategory
              title="Achievement Streaks"
              icon={<Flame className="w-6 h-6 text-orange-500" />}
              entries={leaderboards?.streaks || []}
              loading={loading?.streaks || false}
              emptyMessage="No active streaks yet. Build your streak to appear here!"
            />

            {/* Content Mastery */}
            <LeaderboardCategory
              title="Content Mastery"
              icon={<Target className="w-6 h-6 text-green-500" />}
              entries={leaderboards?.contentMastery || []}
              loading={loading?.contentMastery || false}
              emptyMessage="No mastery data available yet. Complete lessons to appear here!"
            />

            {/* Rising Stars */}
            <LeaderboardCategory
              title="Rising Stars"
              icon={<TrendingUp className="w-6 h-6 text-blue-500" />}
              entries={leaderboards?.risingStars || []}
              loading={loading?.risingStars || false}
              emptyMessage="No rising stars yet. Show your momentum to appear here!"
            />
          </div>

          {/* Motivational Footer */}
          <div className="mt-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-center">
            <Award className="w-12 h-12 text-white mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">
              Keep Climbing the Ranks!
            </h3>
            <p className="text-purple-100 text-lg">
              Every lesson completed, streak maintained, and goal achieved brings you closer to the top
            </p>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CommunityLeaderboardsDashboard;